// Completion for a HollowScript editor - the one genuinely new piece here
// (nothing like it exists anywhere in this repo yet). Two sources, per the
// task brief's own priority order (capability surface first, variable scope
// second):
//
//   1. Capability namespace/function names from the project's actual
//      granted `uses` set (`capabilityCatalog.ts`, built from the real
//      `createStudioCapabilities` factory - never a hand-written list).
//   2. Local variable/parameter/function names in scope at the cursor
//      (`scope.ts`, walked over `@hollow/script`'s own published AST).
//
// Plus keywords and the stdlib, so the list is genuinely useful rather than
// only covering the two priority items. Returns plain data - see
// `tokenize.ts`'s own doc comment for why no `monaco-editor` import belongs
// in this module.

import { parse } from '@hollow/script';
import type { StudioCapabilityName } from '../capabilities.js';
import { capabilityMembers, capabilityNamespaceDetail, grantedCapabilityNames } from './capabilityCatalog.js';
import { collectScopeAt, type ScopeBindingKind } from './scope.js';
import { HOLLOWSCRIPT_KEYWORDS, STDLIB_FREE_FUNCTIONS, STDLIB_METHODS, stdlibMemberName } from './stdlib.js';

export type HollowScriptCompletionKind =
  | 'keyword'
  | 'capability'
  | 'method'
  | 'function'
  | 'variable'
  | 'constant'
  | 'parameter'
  | 'record'
  | 'import';

export interface HollowScriptCompletionItem {
  readonly label: string;
  readonly kind: HollowScriptCompletionKind;
  readonly detail: string | undefined;
  readonly documentation: string | undefined;
  readonly insertText: string;
}

export interface HollowScriptCompletionParams {
  readonly source: string;
  readonly offset: number;
  /** The current line's text up to (not including) the cursor - used only to
   * detect a `name.` member-access context. Monaco's own model already has
   * this cheaply available (`model.getLineContent`), so this module takes a
   * plain string rather than reaching for a model/position type itself. */
  readonly lineTextBeforeCursor: string;
  readonly grantedCapabilities: ReadonlySet<StudioCapabilityName> | undefined;
}

const SCOPE_KIND_TO_COMPLETION_KIND: Readonly<Record<ScopeBindingKind, HollowScriptCompletionKind | null>> = {
  constant: 'constant',
  variable: 'variable',
  parameter: 'parameter',
  function: 'function',
  record: 'record',
  import: 'import',
  // A `uses` name is already offered as a capability item (with its real
  // catalog detail/doc attached); skip it here to avoid a duplicate,
  // undocumented entry.
  capability: null,
};

const MEMBER_ACCESS_RE = /([A-Za-z_][A-Za-z0-9_]*)\.[A-Za-z0-9_]*$/;

function dedupedStdlibMethodNames(): readonly { name: string; signature: string; doc: string }[] {
  const seen = new Set<string>();
  const out: { name: string; signature: string; doc: string }[] = [];
  for (const member of STDLIB_METHODS) {
    const name = stdlibMemberName(member);
    if (seen.has(name)) continue;
    seen.add(name);
    out.push({ name, signature: member.signature, doc: `${member.receiver}.${member.doc}` });
  }
  return out;
}

/** Completions after `receiverName.` - the granted capability's real
 * members when `receiverName` names one, else every stdlib method name as a
 * generic fallback (no static type inference is available to narrow this to
 * the receiver's real type - see `scope.ts`'s own doc comment for why). */
function completionsForMember(receiverName: string, granted: ReadonlySet<StudioCapabilityName> | undefined): readonly HollowScriptCompletionItem[] {
  if ((grantedCapabilityNames(granted) as readonly string[]).includes(receiverName)) {
    const members = capabilityMembers(receiverName);
    if (members.length > 0) {
      return members.map((member) => ({
        label: member.name,
        kind: 'method',
        detail: member.signature,
        documentation: `Capability method - requires \`uses ${receiverName}\`.`,
        insertText: member.name,
      }));
    }
  }
  return dedupedStdlibMethodNames().map(({ name, signature, doc }) => ({
    label: name,
    kind: 'method',
    detail: signature,
    documentation: doc,
    insertText: name,
  }));
}

/** The full completion list at `params.offset`. Never throws: `parse` never
 * does (spec.md gate 2), so an in-progress, syntactically broken edit (the
 * common case while a creator is mid-keystroke) still yields a best-effort
 * scope from whatever parsed cleanly around it. */
export function computeHollowScriptCompletions(params: HollowScriptCompletionParams): readonly HollowScriptCompletionItem[] {
  const memberMatch = MEMBER_ACCESS_RE.exec(params.lineTextBeforeCursor);
  if (memberMatch) {
    const receiverName = memberMatch[1];
    if (receiverName !== undefined) return completionsForMember(receiverName, params.grantedCapabilities);
  }

  const items: HollowScriptCompletionItem[] = [];
  for (const keyword of HOLLOWSCRIPT_KEYWORDS) {
    items.push({ label: keyword, kind: 'keyword', detail: undefined, documentation: undefined, insertText: keyword });
  }
  for (const name of grantedCapabilityNames(params.grantedCapabilities)) {
    items.push({
      label: name,
      kind: 'capability',
      detail: capabilityNamespaceDetail(name),
      documentation: `Granted capability namespace - requires \`uses ${name}\`.`,
      insertText: name,
    });
  }
  for (const fn of STDLIB_FREE_FUNCTIONS) {
    const name = stdlibMemberName(fn);
    items.push({ label: name, kind: 'function', detail: fn.signature, documentation: `${fn.receiver}.${fn.doc}`, insertText: name });
  }

  const { program } = parse(params.source);
  const seen = new Set<string>();
  for (const binding of collectScopeAt(program, params.offset)) {
    const kind = SCOPE_KIND_TO_COMPLETION_KIND[binding.kind];
    if (kind === null) continue;
    const key = `${kind}:${binding.name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ label: binding.name, kind, detail: binding.typeText ?? binding.kind, documentation: undefined, insertText: binding.name });
  }

  return items;
}
